import { VelocityActions } from '../components/VelocityActions';
import { FilesBrowser } from './FilesTab';
import { VelocityVersion } from './VelocityVersion';
import { velocityFilesApi } from '../api/velocity';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import type { BedrockSettings, VelocityView } from '@mcpanel/core';
import { velocityApi } from '../api/velocity';
import { usePoll } from '../hooks/usePoll';
import { errorMessage, useApp } from '../app-context';
import { Dialog, ErrorAlert, StateBadge } from '../components/ui';
import { EditBoundary, useEditDirty } from '../components/EditBoundary';
import { Spinner } from '../components/feedback';
import { ServerSections } from '../components/ServerNavigation';
import { PageTransition } from '../components/motion';
import { VelocityPlugins } from './VelocityPlugins';

function VelocityBedrock({ data, reload }: { data: VelocityView; reload: () => void }) {
  const { canOperate, notify } = useApp();
  const [settings, setSettings] = useState<BedrockSettings>(data.bedrock);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => setSettings(data.bedrock), [data.bedrock.enabled, data.bedrock.port]);
  useEditDirty(settings.enabled !== data.bedrock.enabled || settings.port !== data.bedrock.port);
  async function apply() {
    setBusy(true);
    try {
      await velocityApi.bedrock(settings);
      setConfirm(false);
      reload();
      notify('統合版を反映しました');
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card card-body stack">
      <h2>統合版</h2>
      <p>Geyser-Velocity と Floodgate-Velocity を入口に配置します。統合版も hub から /server で移動できます。</p>
      <label className="check">
        <input
          type="checkbox"
          checked={settings.enabled}
          disabled={!canOperate}
          onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
        />
        統合版を有効にする
      </label>
      <label className="field">
        UDP ポート
        <input
          className="input"
          type="number"
          min={19132}
          max={19231}
          value={settings.port}
          disabled={!canOperate}
          onChange={(e) => setSettings({ ...settings, port: Number(e.target.value) })}
        />
      </label>
      <p>Floodgate の鍵: {data.keyReady ? '取得済み' : '未取得'}</p>
      {canOperate && (
        <button
          className="btn primary"
          disabled={busy || !Number.isInteger(settings.port) || settings.port < 19132 || settings.port > 19231}
          onClick={() => setConfirm(true)}
        >
          反映する
        </button>
      )}
      {confirm && (
        <Dialog
          title="統合版の設定を反映"
          onClose={() => setConfirm(false)}
          actions={
            <button className="btn primary" disabled={busy} onClick={() => void apply()}>
              {busy && <Spinner />}反映して再起動
            </button>
          }
        >
          <p>接続中の全プレイヤーが切断されます。</p>
        </Dialog>
      )}
    </section>
  );
}
function VelocityLogs() {
  const [lines, setLines] = useState(200);
  const result = usePoll(() => velocityApi.logs(lines), 10000, [lines]);
  return (
    <section className="card card-body stack">
      <div className="row">
        <h2>ログ</h2>
        <label>
          行数{' '}
          <select className="input" value={lines} onChange={(e) => setLines(Number(e.target.value))}>
            {[100, 200, 500, 1000].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>
        </label>
        <button className="btn" onClick={() => void result.reload()}>
          更新
        </button>
      </div>
      <ErrorAlert error={result.error} />
      <pre className="console-lines" style={{ maxHeight: 520, margin: 0 }} aria-label="Velocity のログ">
        {result.data?.lines.join('\n') ?? '読み込み中…'}
      </pre>
    </section>
  );
}
function VelocityContent({ data, reload }: { data: VelocityView; reload: () => void }) {
  const [params, setParams] = useSearchParams();
  const sections = [
    { id: 'overview', label: '概要' },
    { id: 'plugins', label: 'プラグイン' },
    { id: 'bedrock', label: '統合版' },
    ...(data.manageSupported
      ? [
          { id: 'files', label: 'ファイル' },
          { id: 'version', label: 'バージョン' },
        ]
      : []),
    { id: 'logs', label: 'ログ' },
  ];
  const tab = sections.some((s) => s.id === params.get('tab')) ? params.get('tab')! : 'overview';
  return (
    <>
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div>
          <div className="row">
            <h1 data-search-id={`velocity-${tab}`}>Velocity（入口）</h1>
            <StateBadge state={data.state} />
          </div>
          <div className="row small muted server-subtitle">
            Velocity {data.version ?? '—'}
            <span>edge VM 101</span>
          </div>
        </div>
        <VelocityActions data={data} reload={reload} />
      </div>
      {data.message && <p className="alert warning">{data.message}</p>}
      <ServerSections sections={sections} section={tab} onChange={(next) => setParams({ tab: next })}>
        <PageTransition key={tab}>
          {tab === 'overview' && (
            <dl className="overview-strip">
              <div className="overview-column">
                <dt className="overview-heading">現在</dt>
                <dd className="overview-content">
                  <dl className="overview-values">
                    <dt>状態</dt>
                    <dd>
                      <StateBadge state={data.state} />
                    </dd>
                    <dt>プレイヤー</dt>
                    <dd className="numeric">{data.network ? data.network.online + ' / ' + data.network.max : '—'}</dd>
                  </dl>
                </dd>
              </div>
              <div className="overview-column">
                <dt className="overview-heading">構成</dt>
                <dd className="overview-content">
                  <dl className="overview-values">
                    <dt>Velocity</dt>
                    <dd>{data.version ?? '—'}</dd>
                    <dt>edge 反映</dt>
                    <dd>
                      {{ applied: '反映済み', pending: '反映待ち', error: 'エラー', unknown: '不明' }[
                        data.edge.state
                      ] ?? data.edge.state}
                    </dd>
                  </dl>
                </dd>
              </div>
              <div className="overview-column">
                <dt className="overview-heading">最終再起動</dt>
                <dd className="overview-content">{data.lastRestart ?? '—'}</dd>
              </div>
            </dl>
          )}
          {tab === 'plugins' && <VelocityPlugins entries={data.entries} reload={reload} />}
          {tab === 'bedrock' && <VelocityBedrock data={data} reload={reload} />}
          {tab === 'files' && data.manageSupported && (
            <FilesBrowser server={{ id: 'velocity', state: data.state }} fileApi={velocityFilesApi} velocity />
          )}
          {tab === 'version' && data.manageSupported && <VelocityVersion current={data.version} reload={reload} />}
          {tab === 'logs' && <VelocityLogs />}
        </PageTransition>
      </ServerSections>
    </>
  );
}
export function Velocity() {
  const result = usePoll(() => velocityApi.get(), 10000);
  return (
    <div className="stack" style={{ gap: 16 }}>
      <Link to="/" className="small muted">
        ← ダッシュボード
      </Link>
      <ErrorAlert error={result.error} />
      {result.data && (
        <EditBoundary resource={{ target: 'velocity', screen: 'mods' }}>
          <VelocityContent data={result.data} reload={() => void result.reload()} />
        </EditBoundary>
      )}
    </div>
  );
}
